import { useAtomValue } from "jotai";
import { useEffect } from "react";

import { clientConfigAtom } from "atoms/clientConfig";
import { Locale } from "types/locale";
import { getUserLocale } from "utils/locale/userLocale";

import { DelightedSurveyObj } from "../types/delightedSurvey";

export type DelightedStartsFeedbackProps = {
  clientId: string;
  sessionId: string;
  surveyType: string;
  initialDelay?: number;
  minTimeOnPage?: number;
};

const SurveyLanguageMapping: Record<Locale, string> = {
  [Locale.Chinese]: "zh-CN",
  [Locale.Dutch]: "nl",
  [Locale.English_GB]: "en",
  [Locale.English_US]: "en",
  [Locale.English_CA]: "en",
  [Locale.French]: "fr",
  [Locale.French_CA]: "fr",
  [Locale.German]: "de",
  [Locale.Italian_IT]: "it",
  [Locale.Japanese_JP]: "ja",
  [Locale.Polish]: "pl",
  [Locale.Portuguese_BR]: "pt-BR",
  [Locale.Portuguese_PT]: "pt",
  [Locale.Spanish_ES]: "es",
  [Locale.Thai]: "th",
};

export const useDelightedSurvey = ({
  clientId,
  sessionId,
  surveyType,
  initialDelay = 0,
  minTimeOnPage = 0,
}: DelightedStartsFeedbackProps): void => {
  const { locale, enableDelightedFeedback } = useAtomValue(clientConfigAtom);

  const surveyDisabled = !enableDelightedFeedback;
  const surveyUrl = `https://d2yyd1h5u9mauk.cloudfront.net/integrations/web/v1/library/g4GP09igdW1Dzg8h/${surveyType}.js`;
  const surveyLanguage = getUserLocale(locale);

  useEffect(() => {
    if (surveyDisabled) {
      return;
    }

    if (!window[surveyType as keyof Window]) {
      (window[surveyType as keyof Window] as DelightedSurveyObj) = {
        survey: () => {},
      };

      const script = document.createElement("script");
      script.src = surveyUrl;
      script.id = "delighted-survey";
      script.onload = () => {
        (window[surveyType as keyof Window] as DelightedSurveyObj).survey({
          name: clientId,
          initialDelay,
          minTimeOnPage,
          properties: {
            locale: SurveyLanguageMapping[surveyLanguage],
            sessionId,
            clientId,
          },
        });
      };
      document.body.appendChild(script);
    }
  },[
    clientId,
    initialDelay,
    minTimeOnPage,
    sessionId,
    surveyDisabled,
    surveyLanguage,
    surveyType,
    surveyUrl
  ]);
};
